import { Api } from "@model";
import { Button, Spin, Timeline } from "antd";
import { DeleteOutlined, SyncOutlined } from "@ant-design/icons";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const { Item: TimelineItem } = Timeline;

const LIMIT = 50;

type UserLogRef = { id: string; name?: string; language?: string };
type UserLog = {
  _id: string;
  user: { _id: string; name?: string };
  action: string;
  toUser?: UserLogRef;
  toCharacter?: UserLogRef;
  date: string;
  oldValue?: Record<string, unknown>;
  toValue?: Record<string, unknown> & { message?: string };
};

function formatChange(o: unknown): string {
  if (o === undefined || o === null) return "—";
  if (typeof o === "string") return o || "—";
  if (typeof o === "boolean") return o ? "yes" : "no";
  if (Array.isArray(o)) return o.join(", ") || "—";
  if (typeof o === "object") return JSON.stringify(o);
  return String(o);
}

function displaysAsDash(o: unknown): boolean {
  if (o === undefined || o === null) return true;
  if (typeof o === "string") return o.trim() === "";
  if (Array.isArray(o))
    return (
      o.length === 0 ||
      o.every((x) => (typeof x === "string" ? x.trim() === "" : false))
    );
  return false;
}

function renderDetails(log: UserLog): React.ReactNode {
  const oldV = log.oldValue as Record<string, unknown> | undefined;
  const toV = log.toValue as Record<string, unknown> | undefined;
  if (!oldV && !toV) return null;

  const detailStyle: React.CSSProperties = {
    marginTop: 6,
    padding: 8,
    background: "#fafafa",
    borderRadius: 6,
    fontSize: 12,
    color: "#555",
    border: "1px solid #eee",
  };

  if (log.action === "message_to_ai" && toV) {
    const response = toV.response;
    const err = toV.error;
    if (response != null && !displaysAsDash(response))
      return (
        <div style={detailStyle}>
          <strong>Answer:</strong> {String(response)}
        </div>
      );
    if (err != null && !displaysAsDash(err))
      return (
        <div style={detailStyle}>Can't answer because ({String(err)})</div>
      );
  }

  if (log.action === "edit_profile" && oldV && toV) {
    const fields: { key: string; label: string }[] = [
      { key: "name", label: "Name" },
      { key: "country", label: "Country" },
      { key: "bio", label: "Bio" },
      { key: "languageLevel", label: "Language level" },
      { key: "targetLanguage", label: "Target language" },
      { key: "originLanguage", label: "Origin language" },
      { key: "goals", label: "Goals" },
      { key: "city", label: "City" },
      { key: "gender", label: "Gender" },
      { key: "jobTitle", label: "Job title" },
    ];
    const rows = fields
      .filter((f) => {
        const o = oldV[f.key];
        const t = toV[f.key];
        if (o === t) return false;
        if (displaysAsDash(o) || displaysAsDash(t)) return false;
        return true;
      })
      .map((f) => (
        <div key={f.key}>
          <strong>{f.label}:</strong> {formatChange(oldV[f.key])} →{" "}
          {formatChange(toV[f.key])}
        </div>
      ));
    if (rows.length === 0) return null;
    return <div style={detailStyle}>{rows}</div>;
  }

  if (log.action === "take_language_test" && (oldV || toV)) {
    const oldL = oldV?.languageLevel;
    const newL = toV?.languageLevel;
    if (displaysAsDash(oldL) || displaysAsDash(newL)) return null;
    return (
      <div style={detailStyle}>
        <strong>Language level:</strong> {formatChange(oldL)} →{" "}
        {formatChange(newL)}
      </div>
    );
  }

  if (log.action === "edit_group" && (oldV || toV)) {
    const groupFields: { key: string; label: string }[] = [
      { key: "chatTitle", label: "Title" },
      { key: "avatar", label: "Avatar" },
      { key: "maximumMemberCount", label: "Max members" },
      { key: "canMembersAddMember", label: "Members can add" },
      { key: "isPublic", label: "Public" },
    ];
    const rows = groupFields
      .filter((f) => {
        const o = oldV?.[f.key];
        const t = toV?.[f.key];
        if (o === t) return false;
        if (displaysAsDash(o) || displaysAsDash(t)) return false;
        return true;
      })
      .map((f) => (
        <div key={f.key}>
          <strong>{f.label}:</strong> {formatChange(oldV?.[f.key])} →{" "}
          {formatChange(toV?.[f.key])}
        </div>
      ));
    if (rows.length === 0) return null;
    return <div style={detailStyle}>{rows}</div>;
  }

  if (log.action === "add_word" && toV) {
    const parts: React.ReactNode[] = [];
    parts.push(<strong>Word:</strong>, " ", formatChange(toV.word));
    if (toV.translate != null && !displaysAsDash(toV.translate))
      parts.push(
        " → ",
        <strong>translate:</strong>,
        " ",
        formatChange(toV.translate),
      );
    parts.push(" → ", <strong>lang:</strong>, " ", formatChange(toV.toLang));
    return <div style={detailStyle}>{parts}</div>;
  }

  if (log.action === "remove_word" && toV) {
    const parts: string[] = [];
    if (toV.word != null) parts.push(`"${formatChange(toV.word)}"`);
    if (toV.toLang != null) parts.push(`lang: ${formatChange(toV.toLang)}`);
    if (toV.translate != null && !displaysAsDash(toV.translate))
      parts.push(`translate: ${formatChange(toV.translate)}`);
    if (parts.length === 0) return null;
    return <div style={detailStyle}>{parts.join(" · ")}</div>;
  }

  if (log.action === "update_word_learn_level" && (oldV || toV)) {
    const oldL = oldV?.learnLevel;
    const newL = toV?.learnLevel;
    if (displaysAsDash(oldL) && displaysAsDash(newL)) return null;
    const word =
      (toV?.word ?? oldV?.word) != null
        ? ` "${formatChange(toV?.word ?? oldV?.word)}"`
        : "";
    return (
      <div style={detailStyle}>
        <strong>Word:</strong>
        {word}
        <div style={{ marginTop: 4 }}>
          <strong>Learn level:</strong> {formatChange(oldL)} →{" "}
          {formatChange(newL)}
        </div>
      </div>
    );
  }

  if (log.action === "feedback" && toV?.message) {
    return (
      <div style={detailStyle}>
        <strong>Message:</strong> {formatChange(toV.message)}
      </div>
    );
  }

  if (
    (log.action === "report_message" || log.action === "report_user") &&
    toV
  ) {
    const parts: React.ReactNode[] = [];
    const category = toV.category as string | undefined;
    if (category)
      parts.push(
        <strong>Reason:</strong>,
        " ",
        category.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      );
    if (toV.description != null && !displaysAsDash(toV.description))
      parts.push(
        parts.length ? " · " : null,
        <strong>Description:</strong>,
        " ",
        formatChange(toV.description),
      );
    if (toV.messageID)
      parts.push(parts.length ? " · " : null, `message ID: ${toV.messageID}`);
    if (toV.chatID)
      parts.push(parts.length ? " · " : null, `chat ID: ${toV.chatID}`);
    if (parts.length === 0) return null;
    return <div style={detailStyle}>{parts}</div>;
  }

  if (log.action === "created_group" && toV) {
    const parts: string[] = [];
    if (toV.chatID) parts.push(`chat ID: ${toV.chatID}`);
    if (toV.chatTitle) parts.push(`"${toV.chatTitle}"`);
    if (toV.memberCount != null) parts.push(`${toV.memberCount} members`);
    if (parts.length === 0) return null;
    return <div style={detailStyle}>{parts.join(" · ")}</div>;
  }

  const lines: string[] = [];
  const allKeys = new Set([
    ...Object.keys(oldV || {}),
    ...Object.keys(toV || {}),
  ]);
  allKeys.forEach((k) => {
    if (k === "message") return;
    const o = oldV?.[k];
    const t = toV?.[k];
    if (o !== undefined || t !== undefined) {
      if (o !== t && !displaysAsDash(o) && !displaysAsDash(t))
        lines.push(`${k}: ${formatChange(o)} → ${formatChange(t)}`);
      else if (o === t && !displaysAsDash(t))
        lines.push(`${k}: ${formatChange(t)}`);
    }
  });
  if (lines.length === 0) return null;
  return (
    <div style={detailStyle}>
      {lines.map((l, i) => (
        <div key={i}>{l}</div>
      ))}
    </div>
  );
}

const actionLabels: Record<string, (log: UserLog) => string> = {
  follow: (l) =>
    `${l.user?.name || l.user?._id} followed ${l.toUser?.name || l.toUser?.id}`,
  unfollow: (l) =>
    `${l.user?.name || l.user?._id} unfollowed ${l.toUser?.name || l.toUser?.id}`,
  message_to_user: (l) =>
    `${l.user?.name || l.user?._id} sent a message to ${l.toUser?.name || l.toUser?.id}`,
  message_to_ai: (l) =>
    `${l.user?.name || l.user?._id} sent a message to character ${l.toCharacter?.name || l.toCharacter?.id}`,
  created_group: (l) => {
    const v = l.toValue as { chatTitle?: string; memberCount?: number };
    return `${l.user?.name || l.user?._id} created group${v?.chatTitle ? ` "${v.chatTitle}"` : ""}${v?.memberCount != null ? ` (${v.memberCount} members)` : ""}`;
  },
  edit_group: (l) => `${l.user?.name || l.user?._id} edited a group`,
  view_profile: (l) =>
    `${l.user?.name || l.user?._id} viewed ${l.toUser?.name || l.toUser?.id}'s profile`,
  edit_profile: (l) => `${l.user?.name || l.user?._id} edited their profile`,
  take_language_test: (l) =>
    `${l.user?.name || l.user?._id} took the language test`,
  blocked_user: (l) =>
    `${l.user?.name || l.user?._id} blocked ${l.toUser?.name || l.toUser?.id}`,
  report_user: (l) =>
    `${l.user?.name || l.user?._id} reported ${l.toUser?.name || l.toUser?.id}`,
  report_message: (l) => `${l.user?.name || l.user?._id} reported a message`,
  add_word: (l) => `${l.user?.name || l.user?._id} added a word`,
  remove_word: (l) => {
    const v = l.toValue as { word?: string; toLang?: string };
    return `${l.user?.name || l.user?._id} removed word${v?.word ? ` "${v.word}"` : ""}`;
  },
  update_word_learn_level: (l) => {
    const v = l.toValue as { word?: string; learnLevel?: number };
    return `${l.user?.name || l.user?._id} changed word learn level${v?.word ? ` for "${v.word}"` : ""}`;
  },
  feedback: (l) => `${l.user?.name || l.user?._id} sent feedback`,
};

function formatLogLabel(log: UserLog): string {
  const fn = actionLabels[log.action];
  return fn ? fn(log) : `${log.user?.name || log.user?._id} — ${log.action}`;
}

const userLink = (id: string, label: string) => (
  <Link to={`/panel/users/edit/${id}`} style={{ color: "#1890ff" }}>
    {label || id}
  </Link>
);
const characterLink = (id: string, label: string) => (
  <Link to={`/panel/characters/messages/${id}`} style={{ color: "#1890ff" }}>
    {label || id}
  </Link>
);

const characterLabelWithLanguage = (char?: UserLogRef): string => {
  if (!char) return "";
  const name = char.name || char.id || "";
  if (!char.language) return name;
  return `${name} (${char.language})`;
};

function renderLogLabel(log: UserLog): React.ReactNode {
  const u = log.user?.name || log.user?._id || "";
  const uid = log.user?._id;
  const toU = log.toUser?.name || log.toUser?.id || "";
  const toUid = log.toUser?.id;
  const toC = characterLabelWithLanguage(log.toCharacter);
  const toCid = log.toCharacter?.id;
  const action = log.action;
  if (action === "follow")
    return (
      <>
        {uid ? userLink(uid, u) : u} followed{" "}
        {toUid ? userLink(toUid, toU) : toU}
      </>
    );
  if (action === "unfollow")
    return (
      <>
        {uid ? userLink(uid, u) : u} unfollowed{" "}
        {toUid ? userLink(toUid, toU) : toU}
      </>
    );
  if (action === "message_to_user")
    return (
      <>
        {uid ? userLink(uid, u) : u} sent a message to{" "}
        {toUid ? userLink(toUid, toU) : toU}
      </>
    );
  if (action === "message_to_ai")
    return (
      <>
        {uid ? userLink(uid, u) : u} sent a message to character{" "}
        {toCid ? characterLink(toCid, toC) : toC}
      </>
    );
  if (action === "view_profile")
    return (
      <>
        {uid ? userLink(uid, u) : u} viewed {toUid ? userLink(toUid, toU) : toU}
        's profile
      </>
    );
  if (action === "blocked_user")
    return (
      <>
        {uid ? userLink(uid, u) : u} blocked{" "}
        {toUid ? userLink(toUid, toU) : toU}
      </>
    );
  if (action === "report_user")
    return (
      <>
        {uid ? userLink(uid, u) : u} reported{" "}
        {toUid ? userLink(toUid, toU) : toU}
      </>
    );
  if (action === "created_group") {
    const v = log.toValue as { chatTitle?: string; memberCount?: number };
    return (
      <>
        {uid ? userLink(uid, u) : u} created group
        {v?.chatTitle ? ` "${v.chatTitle}"` : ""}
        {v?.memberCount != null ? ` (${v.memberCount} members)` : ""}
      </>
    );
  }
  if (action === "edit_group")
    return <>{uid ? userLink(uid, u) : u} edited a group</>;
  if (action === "edit_profile")
    return <>{uid ? userLink(uid, u) : u} edited their profile</>;
  if (action === "take_language_test")
    return <>{uid ? userLink(uid, u) : u} took the language test</>;
  if (action === "report_message")
    return <>{uid ? userLink(uid, u) : u} reported a message</>;
  if (action === "add_word")
    return <>{uid ? userLink(uid, u) : u} added a word</>;
  if (action === "remove_word") {
    const v = log.toValue as { word?: string };
    return (
      <>
        {uid ? userLink(uid, u) : u} removed word{v?.word ? ` "${v.word}"` : ""}
      </>
    );
  }
  if (action === "update_word_learn_level") {
    const v = log.toValue as { word?: string };
    return (
      <>
        {uid ? userLink(uid, u) : u} changed word learn level
        {v?.word ? ` for "${v.word}"` : ""}
      </>
    );
  }
  if (action === "feedback")
    return <>{uid ? userLink(uid, u) : u} sent feedback</>;
  return formatLogLabel(log);
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleString();
}

export const UserLogTimeline = ({
  userId,
  onLogCountChange,
}: {
  userId?: string;
  onLogCountChange?: (count: number) => void;
}) => {
  const [logs, setLogs] = useState<UserLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const loadingMoreRef = useRef(false);

  const load = useCallback(
    async (before?: string) => {
      if (before) {
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
      } else setLoading(true);
      try {
        const params = new URLSearchParams({ limit: String(LIMIT) });
        if (before) params.set("before", before);
        if (userId) params.set("userId", userId);
        const res = await Api.get<{ data: UserLog[] }>(
          `/api/admin/panel/user-logs?${params}`,
        );
        const list = res.data?.data ?? [];
        if (before) {
          setLogs((prev) => [...prev, ...list]);
        } else {
          setLogs(list);
        }
        setHasMore(list.length === LIMIT);
      } finally {
        if (before) loadingMoreRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [userId],
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !hasMore || loadingMore || logs.length === 0) return;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollHeight - scrollTop - clientHeight < 200) {
        const last = logs[logs.length - 1];
        if (last?.date) {
          load(last.date);
        }
      }
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [load, logs, hasMore, loadingMore]);

  useEffect(() => {
    onLogCountChange?.(logs.length);
  }, [logs.length, onLogCountChange]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        maxHeight: "80vh",
        overflowY: "auto",
        paddingRight: 8,
        paddingTop: 8,
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <Button
          type="default"
          icon={<SyncOutlined />}
          onClick={() => load()}
          size="small"
        >
          Refresh
        </Button>
      </div>
      <Timeline>
        {logs.map((log) => (
          <TimelineItem key={log._id} color="blue">
            <div style={{ position: "relative", paddingRight: 28 }}>
              <span
                role="button"
                tabIndex={0}
                onClick={() => {
                  Api.delete(`/api/admin/panel/user-logs/${log._id}`)
                    .then(() =>
                      setLogs((prev) => prev.filter((l) => l._id !== log._id)),
                    )
                    .catch(() => {});
                }}
                onKeyDown={(e) =>
                  e.key === "Enter" && (e.currentTarget as HTMLElement).click()
                }
                style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  color: "#999",
                  cursor: "pointer",
                  fontSize: 14,
                  padding: 4,
                }}
                title="Delete log"
                onMouseOver={(e) => {
                  e.currentTarget.style.color = "#ff4d4f";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.color = "#999";
                }}
              >
                <DeleteOutlined />
              </span>
              <div style={{ fontWeight: 500 }}>{renderLogLabel(log)}</div>
              {(log.action === "message_to_user" ||
                log.action === "message_to_ai") &&
                log.toValue?.message && (
                  <div
                    style={{
                      marginTop: 4,
                      padding: 8,
                      background: "#f5f5f5",
                      borderRadius: 6,
                      fontSize: 13,
                      color: "#333",
                    }}
                  >
                    {String(log.toValue.message)}
                  </div>
                )}
              {renderDetails(log)}
              <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                {formatDate(log.date)}
              </div>
            </div>
          </TimelineItem>
        ))}
      </Timeline>
      {loadingMore && (
        <div style={{ textAlign: "center", padding: 16 }}>
          <Spin />
        </div>
      )}
    </div>
  );
};
